import { useMemo } from 'react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectIndicator,
  SelectPopover,
  ListBox,
  Label,
  FieldError
} from '@heroui/react';
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps
} from 'react-hook-form';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';

export type FieldSelectProps<
  T extends FieldValues = FieldValues,
  I extends object = object
> = Omit<UseControllerProps<T>, 'name' | 'control'> & {
  accessor?: (value: unknown) => string | null | undefined;
  path: Path<T>;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
  className?: string;
  // A list of rows renders through the child function, one call per row; static
  // children stay a plain node.
  items?: Iterable<I>;
  children?: React.ReactNode | ((item: I) => React.ReactElement);
  selectedKey?: string | null;
  [key: string]: unknown;
};

export const FieldSelectBase = <
  T extends FieldValues = FieldValues,
  I extends object = object
>({
  accessor,
  items,
  children,
  label,
  placeholder,
  isRequired,
  className,
  ...props
}: FieldSelectProps<T, I>) => {
  const { path } = props;
  const {
    field: { disabled, value: fieldValue, ...fieldProps },
    fieldState: { error }
  } = useController({
    name: path
  });

  const value = useMemo(
    () => (accessor ? accessor(fieldValue) : fieldValue),
    [accessor, fieldValue]
  );

  return (
    <Select
      {...fieldProps}
      isInvalid={!!error}
      isDisabled={disabled}
      isRequired={isRequired}
      placeholder={placeholder}
      value={value ?? null}
      className={className}
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      <SelectTrigger>
        <SelectValue />
        <SelectIndicator />
      </SelectTrigger>
      <SelectPopover>
        <ListBox items={items}>{children}</ListBox>
      </SelectPopover>
      <FieldError>{error?.message}</FieldError>
    </Select>
  );
};

export const FieldSelect = <
  T extends FieldValues = FieldValues,
  I extends object = object
>(
  props: FieldSelectProps<T, I>
) => useGlobalComponent('FieldSelect', FieldSelectBase, props);
